import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Typography, Segmented } from 'antd';
import { useTripTaskStore } from 'shared/stores';
import { isAddressPopulated } from 'shared/utils/address';
import { ELocationMode } from 'components/trips/trip-create-task-modal/types';
import { CoordinatesForm } from '../coordinates-form';
import { SharedAddressFields } from '../shared-address-fields';

export const CustomsTaskForm = () => {
  const { t } = useTranslation('trip');

  const {
    coordinates,
    address,
    loadingCustomAddress,
    unloadingCustomAddress,
    setLocationMode,
    setCoordinates,
    setAddress,
    locationMode,
    creationMode,
    cargoTrips,
  } = useTripTaskStore();

  const isSingleCargo = cargoTrips.length === 1;
  const isMultipleCargosSeparateTasks = cargoTrips.length > 1 && creationMode === 'separate_tasks';

  const hasLoadingCustomAddress = isMultipleCargosSeparateTasks
    ? cargoTrips.some((cargoTrip) => cargoTrip.cargo.loadingCustomAddress)
    : isAddressPopulated(loadingCustomAddress);

  const hasUnloadingCustomAddress = isMultipleCargosSeparateTasks
    ? cargoTrips.some((cargoTrip) => cargoTrip.cargo.unloadingCustomAddress)
    : isAddressPopulated(unloadingCustomAddress);

  const buildOptions = () => {
    const customOptions = [];
    if (hasLoadingCustomAddress) {
      customOptions.push({
        label: t('location_mode_loading_custom_address'),
        value: ELocationMode.loadingCustomAddress,
      });
    }
    if (hasUnloadingCustomAddress) {
      customOptions.push({
        label: t('location_mode_unloading_custom_address'),
        value: ELocationMode.unloadingCustomAddress,
      });
    }

    const regularOptions = [
      { label: t('location_mode_address'), value: ELocationMode.address },
      { label: t('location_mode_coordinates'), value: ELocationMode.coordinates },
    ];

    if (isSingleCargo) {
      return [...customOptions, ...regularOptions];
    }

    if (isMultipleCargosSeparateTasks) {
      return customOptions.length > 0 ? customOptions : regularOptions;
    }

    return regularOptions;
  };

  const options = buildOptions();

  useEffect(() => {
    const currentModeExists = options.some((opt) => opt.value === locationMode);
    if (!currentModeExists && options.length > 0) {
      setLocationMode(options[0].value);
    }
  }, [options, locationMode, setLocationMode]);

  const renderForm = () => {
    if (isMultipleCargosSeparateTasks) {
      if (locationMode === ELocationMode.loadingCustomAddress) {
        return (
          <Typography.Text type='secondary'>{t('customs_loading_custom_address_from_cargo_message')}</Typography.Text>
        );
      }
      if (locationMode === ELocationMode.unloadingCustomAddress) {
        return (
          <Typography.Text type='secondary'>{t('customs_unloading_custom_address_from_cargo_message')}</Typography.Text>
        );
      }
    }

    if (locationMode === ELocationMode.loadingCustomAddress) {
      return <SharedAddressFields value={loadingCustomAddress} disabled />;
    }
    if (locationMode === ELocationMode.unloadingCustomAddress) {
      return <SharedAddressFields value={unloadingCustomAddress} disabled />;
    }

    if (locationMode === ELocationMode.coordinates) {
      return <CoordinatesForm value={coordinates} onChange={setCoordinates} />;
    }
    return <SharedAddressFields value={address} onChange={setAddress} />;
  };

  return (
    <Flex gap='large' vertical>
      <Typography.Text type='secondary'>{t('select_location_mode')}</Typography.Text>
      <Segmented options={options} value={locationMode} block onChange={(val) => setLocationMode(val)} />
      {renderForm()}
    </Flex>
  );
};
